// client/src/components/FeedbackModal.tsx
import { CheckCircle, XCircle, AlertTriangle, Info, X } from "lucide-react";

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message: string;
  autoClose?: boolean;
  autoCloseDuration?: number;
}

export default function FeedbackModal({
  isOpen,
  onClose,
  type,
  title,
  message,
  autoClose = true,
  autoCloseDuration = 3000
}: FeedbackModalProps) {
  
  // Auto close after duration
  React.useEffect(() => {
    if (isOpen && autoClose) {
      const timer = setTimeout(onClose, autoCloseDuration);
      return () => clearTimeout(timer);
    }
  }, [isOpen, autoClose, autoCloseDuration, onClose]);

  if (!isOpen) return null;

  const getStyles = () => {
    switch (type) {
      case 'success':
        return {
          icon: <CheckCircle className="w-12 h-12 text-green-500" />,
          iconBg: 'bg-green-100 dark:bg-green-900/20',
          borderColor: 'border-green-200 dark:border-green-800'
        };
      case 'error':
        return {
          icon: <XCircle className="w-12 h-12 text-red-500" />,
          iconBg: 'bg-red-100 dark:bg-red-900/20',
          borderColor: 'border-red-200 dark:border-red-800'
        };
      case 'warning':
        return {
          icon: <AlertTriangle className="w-12 h-12 text-yellow-500" />,
          iconBg: 'bg-yellow-100 dark:bg-yellow-900/20',
          borderColor: 'border-yellow-200 dark:border-yellow-800'
        };
      case 'info':
        return {
          icon: <Info className="w-12 h-12 text-blue-500" />,
          iconBg: 'bg-blue-100 dark:bg-blue-900/20',
          borderColor: 'border-blue-200 dark:border-blue-800'
        };
    }
  };

  const styles = getStyles();

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black bg-opacity-50 backdrop-blur-sm animate-in fade-in duration-200">
      <div className={`relative w-full max-w-md rounded-xl shadow-2xl bg-white dark:bg-[#333436] animate-in zoom-in-95 duration-300 border-2 ${styles.borderColor}`}>
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-lg transition hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-400"
        >
          <X size={20} />
        </button>

        {/* Content */}
        <div className="p-8 text-center">
          {/* Icon */}
          <div className="flex justify-center mb-6">
            <div className={`p-4 rounded-full ${styles.iconBg}`}>
              {styles.icon}
            </div>
          </div>

          {/* Title */}
          <h3 className="text-2xl font-bold mb-3 text-gray-900 dark:text-white">
            {title}
          </h3>

          {/* Message */}
          <p className="text-base text-gray-600 dark:text-gray-300 mb-6">
            {message}
          </p>

          {/* OK Button */}
          <button
            onClick={onClose}
            className="w-full px-6 py-3 rounded-lg font-medium bg-gray-900 dark:bg-blue-600 text-white hover:bg-gray-800 dark:hover:bg-blue-700 transition"
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
}

// Import React at the top
import React from 'react';